import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfBrush,
  PdfFont,
  PageGen,
} from '../../pdf/pdf';
import { ILayoutLabelGen } from '../layoutLabel';
import { IItemCount } from '@educraft/base';
import { OptsLabel } from '../optsLabel';
import { GUtil } from '../../pdf/gutil';
import { GLabel } from '../../pdf/glabel';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/label';

/**
 * Cesticka (Small Path) - Spaghetti path puzzle
 *
 * Layout: 5 landscape pages, each with increasing path complexity.
 * Top row: 10 shuffled question labels.
 * Middle: tangled connecting paths.
 * Bottom row: answer labels (1 or 3 choices per position).
 *
 * Migrated from C# EduFrame.Img10.Generators.CestickaBase
 */
function generateCesticka(answers: number) {
  return async (pdf: PdfGen, opts: OptsLabel): Promise<void> => {
    const margin = PdfUnit.FromMillimeters(10);
    const itemCount = opts.list.length;
    const size = (PdfSize.A4H.width - 2 * margin) / itemCount;
    const answerSize = 40;

    const randomMap = GUtil.genMap(opts.data.random, opts.list.length);

    // Build answer choices for each position
    const allRight = opts.list.map((item) => item.right);
    const ans: GLabel[][] = allRight.map((correct) => {
      // Shuffle all other B labels
      const others = allRight.filter((k) => k !== correct);
      for (let i = others.length - 1; i > 0; i--) {
        const j = opts.data.random.next(0, i + 1);
        [others[i], others[j]] = [others[j], others[i]];
      }
      // Take correct + (answers-1) wrong, then shuffle
      const choices = [correct, ...others].slice(0, answers);
      for (let i = choices.length - 1; i > 0; i--) {
        const j = opts.data.random.next(0, i + 1);
        [choices[i], choices[j]] = [choices[j], choices[i]];
      }
      return choices;
    });

    const pages: PageGen[] = [];
    for (let i = 0; i < 5; i++) {
      pages.push(pdf.addPage(true));
    }

    for (let count = 0; count < 5; count++) {
      const page = pages[count];
      const rect = new PdfRect(
        margin,
        margin + size,
        PdfSize.A4H.width - 2 * margin,
        PdfSize.A4H.height - 2 * margin - size - answerSize * answers
      );

      page.drawRectangle(GUtil.basePen, null, rect);

      const lanes = count + 2;

      // Random starting lanes for top and bottom
      const t0 = Array.from({ length: opts.list.length }, () => opts.data.random.next(0, lanes));
      const b0 = Array.from({ length: opts.list.length }, (_, i) => {
        const exclude = t0[randomMap[i]];
        let r: number;
        do {
          r = opts.data.random.next(0, lanes);
        } while (r === exclude);
        return r;
      });

      // Convert to global vertical positions
      const t1 = t0.map((v, i) => i * lanes + v);
      const b1 = b0.map((v, i) => randomMap[i] * lanes + v);

      const usedV = new Set([...t1, ...b1]);
      const allV = Array.from({ length: opts.list.length * lanes }, (_, i) => i).filter(
        (i) => !usedV.has(i)
      );
      for (let i = allV.length - 1; i > 0; i--) {
        const j = opts.data.random.next(0, i + 1);
        [allV[i], allV[j]] = [allV[j], allV[i]];
      }

      const allH = Array.from({ length: opts.list.length * (count + 1) }, (_, i) => i);
      for (let i = allH.length - 1; i > 0; i--) {
        const j = opts.data.random.next(0, i + 1);
        [allH[i], allH[j]] = [allH[j], allH[i]];
      }

      let vIdx = 0;
      let hIdx = 0;

      const hx: number[][] = [];
      for (let i = 0; i <= count; i++) {
        hx.push(allH.slice(hIdx, hIdx + opts.list.length).map((j) => j + 1));
        hIdx += opts.list.length;
      }

      const vx: number[][] = [];
      for (let i = 0; i < count; i++) {
        vx.push(allV.slice(vIdx, vIdx + opts.list.length));
        vIdx += opts.list.length;
      }

      const v: number[][] = [t1, ...vx, b1];
      const h: number[][] = [
        Array.from({ length: opts.list.length }, () => 0),
        ...hx,
        Array.from({ length: opts.list.length }, () => 1 + (count + 1) * itemCount),
      ];

      const X = (i: number) =>
        rect.x + Math.floor(i / lanes) * size + (1 + (i % lanes)) * (size / (lanes + 1));
      const Y = (i: number) => rect.y + (rect.height / ((count + 1) * itemCount + 1)) * i;

      // Collect all left label texts for font adjustment
      const leftTexts = opts.list.map((item) => item.left.getText()).filter((t) => t);
      const leftFont =
        leftTexts.length > 0
          ? page.adjustFontMulti(
              leftTexts.map((t) => [t]),
              GUtil.baseFont,
              new PdfSize(size, size)
            )
          : GUtil.baseFont;

      // Collect all right label texts for font adjustment
      const rightTexts = opts.list.map((item) => item.right.getText()).filter((t) => t);
      const labelFont =
        rightTexts.length > 0 ? page.adjustFont(size, rightTexts, GUtil.baseFont) : GUtil.baseFont;

      for (let j = 0; j < opts.list.length; j++) {
        // Top: question label
        const r1 = new PdfRect(margin + j * size, margin, size, size);
        if (randomMap[j] < opts.list.length) {
          page.drawLabel(opts.list[randomMap[j]].left, { rect: r1, font: leftFont });
        }
        page.drawRectangle(GUtil.basePen, null, r1);

        // Bottom: answer labels
        const r2 = new PdfRect(margin + j * size, rect.y + rect.height, size, answers * answerSize);
        page.drawRectangle(GUtil.basePen, null, r2);

        for (let a = 0; a < answers; a++) {
          const r3 = new PdfRect(
            margin + j * size,
            rect.y + rect.height + a * answerSize,
            size,
            answerSize
          );
          if (j < ans.length && a < ans[j].length) {
            page.drawLabel(ans[j][a], { rect: r3, font: labelFont });
          }
        }

        // Path lines
        const points: PdfPoint[] = [];
        for (let i = 0; i <= count; i++) {
          points.push(new PdfPoint(X(v[i][j]), Y(h[i][j])));
          points.push(new PdfPoint(X(v[i][j]), Y(h[i + 1][j])));
          points.push(new PdfPoint(X(v[i + 1][j]), Y(h[i + 1][j])));
        }
        points.push(new PdfPoint(X(v[count + 1][j]), Y(h[count + 2][j])));

        page.drawLines(GUtil.basePen, points);
      }
    }
  };
}

/**
 * Cesticka__Zaklad - Basic path puzzle (1 answer per position)
 */
export class Cesticka__Base implements ILayoutLabelGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 5;
  }
  countMax(): number {
    return 15;
  }

  public generate = generateCesticka(1);
}

/**
 * Cesticka__Kviz - Quiz path puzzle (3 answer choices per position)
 */
export class Cesticka__Quiz implements ILayoutLabelGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 5;
  }
  countMax(): number {
    return 15;
  }

  public generate = generateCesticka(3);
}
